import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { propertyApi } from '../services/BaseApiService.jsx';

const ReportLayoutContext = createContext(null);

export function ReportLayoutProvider({ children }) {
  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadLayout() {
      setLoading(true);
      setError(null);
      try {
        const resp = await propertyApi.get('/report-layout', {
          params: { layout_key: 'print_default' },
        });
        if (!isMounted) return;
        const data = resp?.data || null;
        if (data && typeof data === 'object') {
          setLayout(data);
        } else {
          setLayout(null);
          setError('Layout de relatório inválido (payload não é um objeto).');
        }
      } catch (e) {
        console.error('[ReportLayout] Erro ao carregar layout de relatório:', e);
        if (!isMounted) return;
        setError('Erro ao carregar layout de relatório.');
        setLayout(null);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadLayout();

    return () => {
      isMounted = false;
    };
  }, []);

  const helpers = useMemo(() => {
    const sections = layout?.sections || {};
    const page = layout?.page || null;

    const getPageSettings = () => page || null;

    const getSectionLayout = (sectionKey) => {
      if (!sectionKey) return null;
      const section = sections[sectionKey];
      if (!section || typeof section !== 'object') return null;
      return section;
    };

    const getWidgetLayout = (sectionKey, widgetKey) => {
      if (!sectionKey || !widgetKey) return null;
      const section = sections[sectionKey];
      if (!section || typeof section !== 'object') return null;
      const widgets = section.widgets || {};
      const widget = widgets[widgetKey];
      if (!widget || typeof widget !== 'object') return null;
      return widget;
    };

    return {
      getPageSettings,
      getSectionLayout,
      getWidgetLayout,
    };
  }, [layout]);

  const value = useMemo(
    () => ({
      layout,
      loading,
      error,
      ...helpers,
    }),
    [layout, loading, error, helpers],
  );

  return <ReportLayoutContext.Provider value={value}>{children}</ReportLayoutContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReportLayoutConfig() {
  const ctx = useContext(ReportLayoutContext);
  if (!ctx) {
    throw new Error('useReportLayoutConfig deve ser usado dentro de um ReportLayoutProvider');
  }
  return ctx;
}

export default ReportLayoutContext;
